/* eslint-disable max-lines */
import {
  Injectable,
  Injector,
  Type,
  InjectionToken,
  AbstractType,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  ObservableInput,
} from 'rxjs';
import {
  isFunction,
  merge,
  get,
  isEmpty,
  map,
  isEqual,
  forEach,
  filter,
  sortBy,
  toLower,
  set,
  first,
  countBy,
  pick,
  chain,
  find,
  size,
  isNil,
} from 'lodash';

import { ILimit } from '@app/shared/types/common/limit.type';
import { getSubtractDateString, toDate } from '@app/shared/utils/date-utils';
import {
  ONLY_DATE_SUBSTRING_LENGTH,
  MAX_PAGE_SIZE,
  ENTITY_ID_PATH,
} from '@config/global-constants';
import { lazyInject } from '@app/containers/base/decorators';
import { CompanyUtilService } from '../company';
import {
  EntityId,
  IModalDialogOptions,
  ENTITY_TYPES,
  IModalDialogResult,
  IFormAction,
  IFilterQuery,
  ISiteResponse,
  ISite,
  ListColumn,
  FromAndToSiteId,
  IBaseEntityResponse,
  IQohInventoryItemVenue,
  IQuantityOnHandResponse,
  IBaseEntityType,
  IAPIErrorType,
  ITransfer,
  NotificationType,
  IInlineLabelOptions,
  GridRowData,
  XuxGridActionPayload,
  InitialFactorAndCountValue,
  ToolbarButtonDefinition,
  IEvent,
  MEASURE_TRANSACTION,
  XuxGridAction,
} from '@app/shared/types';
import { UiActions } from '@app/store/actions';
import { DataSource } from '../datasource/entity-data-source.service';
import { EntityServiceProvider } from '../datasource/entity-service-provider.service';
import { SitesService } from '../sites/sites.service';
import { rx, observableCombineLatest, observableOf } from '@app/shared/rx';
import {
  getEventRelatedAndWarehouseSites,
  setPrimaryAndWarehouseTop,
  isSiteTypePrimaryWarehouse,
  addStoreNumberForSites,
  calculateUnitCost,
  isSiteTypeWarehouse,
  createPendingQuantityMap,
  getCurrentItemQoh,
  handleTransferUnitChange,
  updateInitialFactorAndCountValue,
  appendInitialFactorAndCountValue,
} from '../transfer/transfer.util';
import { XprtDataService } from '../xprt-data/xprt-data.service';
import {
  setFormFieldValue,
  getFormFieldValue,
  getFormControl,
} from '@app/components/form/services/schema-form/schema-form-functions';
import { GridApi, ICellRendererParams } from 'ag-grid-community';
import { VISIBLE_TRANSFER_GRID_ROW_COUNT } from '../transfer/transfer-constants';
import { NumberFormatService } from '../number-format';
import { StateService } from '../state-service';
import { InventoryItemVenueService } from '../inventory-item-venue/inventory-item-venue.service';
import { InventoryItemVenue } from '@geniuspos/xenial-data-contracts/types/dm-entities/inventory-item-venue';
import { decimalNumber } from '@app/shared/common/decimal-number/decimal-number';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import {
  getEventSalesStartDateTime,
  partitionAndSortItemsByChargeable,
  generateObjectId,
  getDefaultMeasure,
  getBaseMeasure,
  generateUnitTextV2,
} from '@app/shared/utils';
import { TransferService } from '../transfer/transfer.service';
import {
  ITransferCreateOptions,
  PredicateShowsItemFunction,
  ITransferCreateFormAddition,
  IItemsGridFilter,
} from './tiny-transfer.types';
import { ItemShowConditionType } from './tiny-transfer.enums';
import { getTinyDurTransferDefinition } from './tiny-during-transfer.modal-definition';
import { getShowingItemsFilter } from './inventory-items-grid.utils';
import { getInventoryItemVenuesRequireValidator } from './inventory-items.validators';
import { TRANSFER_STATUS_VALUE } from '@app/shared/types/enums/transfer-status.enum';
import {
  BTN_YES,
  BTN_CANCEL,
} from '@app/text-content/button-templates/button-templates-content';
import {
  EDITOR_MESSAGES,
  TOASTER_ERROR_MESSAGE,
  TOASTER_SUCCESS_MESSAGE,
} from '@app/shared/constants/editor-messages';
import { MODAL_TEMPLATES } from '@app/text-content';
import { fromPromise } from 'rxjs/internal-compatibility';
import {
  ReplenishResultType,
  IReplenishResult,
  ReplenishType,
} from '../transfer-replenish';
import { TRANSFER_FORM_FIELD_PATHS } from '../transfer/transfer-form-field-paths';
import { DmDataService } from '../dm-data/dm-data.service';
import { TransferQtyService } from '../transfer-common/transfer-qty.service';
import { ReplenishWorkerBuilder } from '../replenish-worker/replenish-worker.builder';
import {
  IReplenishDestination,
  REPLENISH_SCOPE_OPTIONS_VALUES,
} from '../replenish-worker';
import { REPLENISH_CONFIG_MAPPING } from '../transfer/transfer-replenish.constants';
import { getAllRowsAfterFilterAndSort } from '@components/xvi-grid/custom-components/cell-renderer-components/renderer-helpers';
import { ScanningService } from '../scanning/scanning.service';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';

@Injectable()
export class TinyDuringTransferFormService {
  @lazyInject(XVII18nService)
  public i18nService: XVII18nService;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(CompanyUtilService) public companyService: CompanyUtilService;
  @lazyInject(EntityServiceProvider)
  public entityServiceProvider: EntityServiceProvider;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(XprtDataService) public xrptDataService: XprtDataService;
  @lazyInject(DmDataService) public dmDataService: DmDataService;
  @lazyInject(TransferService) public transferService: TransferService;
  @lazyInject(TransferQtyService)
  public transferQtyService: TransferQtyService;
  @lazyInject(ReplenishWorkerBuilder)
  public replenishWorkerBuilder: ReplenishWorkerBuilder;
  @lazyInject(ScanningService)
  public scanningService: ScanningService;
  public listGridApi: GridApi;
  public isShowAllItems: boolean = false;
  public allInventoryItems: IQohInventoryItemVenue[] = [];
  // This subscriptions array will be clear after close modal
  protected subscriptions: Subscription[] = [];

  /**
   * modal form instance
   * will reassign to current modal form when modal form ready,and will set to null when close modal
   */
  public form: FormGroup;
  public transactionDateLimit: ILimit<string | null> = {
    minimumObs: new BehaviorSubject(
      getSubtractDateString(toDate().toISOString(), ONLY_DATE_SUBSTRING_LENGTH),
    ),
    maximumObs: new BehaviorSubject(null),
  };

  public constructor(public injector: Injector) {}

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  /**
   * use this to prompt tiny during transfer form popup
   *
   * @param options prompt transfer form options
   */
  public async promptModal(
    options: ITransferCreateOptions,
  ): Promise<IModalDialogResult> {
    let optionsWithLatestEvent = options;
    const latestEventInfo = await this.prepareEventInfo(options);

    if (!isEmpty(latestEventInfo)) {
      optionsWithLatestEvent = {
        ...options,
        event: { ...latestEventInfo },
      };
    }

    const modalOptions = this.buildModalOptions(optionsWithLatestEvent);
    const confirmed = await this.uiActions.promptFormModal(modalOptions);

    this.onModalClose();

    return confirmed;
  }

  public onModalClose(): void {
    this.form = null;

    forEach(this.subscriptions, (subs) => {
      subs.unsubscribe();
    });

    this.subscriptions = [];
    this.isShowAllItems = false;
    this.allInventoryItems = [];
    this.scanningService.destroyServiceDefinition();
  }

  public async prepareEventInfo(
    options: ITransferCreateOptions,
  ): Promise<IEvent> {
    this.uiActions.enableLoader();

    let latestEventInfo: IEvent;

    try {
      latestEventInfo = (await this.xrptDataService
        .getEventDetailById(get(options, 'event.id'), {
          select: [
            'event_type_entity_id',
            'id',
            'name',
            'event_days',
            'status',
            'available_sites',
          ],
        })
        .pipe(rx.first())
        .toPromise()) as IEvent;
    } catch (error) {
    } finally {
      this.uiActions.disableLoader();
    }

    return latestEventInfo;
  }

  public buildModalOptions(
    options: ITransferCreateOptions,
  ): IModalDialogOptions {
    const isTransferIn = options.isTransferIn;
    const event = options.event;
    const transferDateTimeUTC = getEventSalesStartDateTime(event);
    const transferDate = this.companyService.convertUTCDateWithTimeZone(
      transferDateTimeUTC,
      true,
    );
    const siteId = isTransferIn ? null : options.site_id;
    const siteIdTo = isTransferIn ? options.site_id : null;
    const siteName = options.site_name;

    // trigger loading immediately
    const isLoadingItems$ = new BehaviorSubject<boolean>(false);
    const itemsGrid$ = new Subject<XuxEditGridComponent>();
    const gridApi$ = new Subject<GridApi>();
    const showItemsCaptionChange$ = new Subject<boolean>();
    const isShowAllItemsObs = showItemsCaptionChange$.pipe(
      rx.scan((isShowAllItems: boolean) => !isShowAllItems, null),
      rx.startWith(false),
    );
    const showItemsCaption$ = isShowAllItemsObs.pipe(
      rx.map((isShowAllItems) => {
        this.isShowAllItems = isShowAllItems;

        return this.i18nService.instant(
          isShowAllItems
            ? I18n.Forms.TransferForm.ShowSelectedItems
            : I18n.Forms.TransferForm.ShowAllItems,
        );
      }),
    );
    const replenishButtonDisabled$: BehaviorSubject<boolean> =
      new BehaviorSubject(true);
    const clearQtyRequestBtnDisable$: BehaviorSubject<boolean> =
      new BehaviorSubject(true);

    const siteDataSource = this.getSiteDataSource(options);

    const showsConditionMapping: Record<
      ItemShowConditionType,
      PredicateShowsItemFunction<IQohInventoryItemVenue>
    > = {
      [ItemShowConditionType.Selected]: getShowingItemsFilter(
        false,
        map(filter(options.inventory_items, { selected: true }), 'entity_id'),
      ),
      [ItemShowConditionType.All]: getShowingItemsFilter(
        false,
        map(options.inventory_items, 'entity_id'),
      ),
    };

    const inlineLabel$ = new Subject<IInlineLabelOptions[]>();

    const formAddition: ITransferCreateFormAddition = {
      isLoadingItems$,
      itemsGrid$,
      gridApi$,
      showItemsCaption$,
      showItemsCaptionChange$,
      siteDataSource,
      inlineLabel$,
      replenishButtonDisabled$,
      clearQtyRequestBtnDisable$,
      itemsGridFilter$: isShowAllItemsObs.pipe(
        rx.map((isShowAllItems) => {
          const conditionType = isShowAllItems
            ? ItemShowConditionType.All
            : ItemShowConditionType.Selected;

          return {
            itemShowsCondition: showsConditionMapping[conditionType],
          };
        }),
      ),
      needEventListener: true,
    };

    const formEventHandler = {
      beforeFormClose: this.handleFooterAction.bind(this),
    };

    return getTinyDurTransferDefinition(
      this,
      {
        formAddition,
        createOptions: options,
        formInitData: {
          transferDate,
          transferDateTimeUTC,
          siteId,
          siteIdTo,
          siteName,
          eventTypeEntityId: get(event, 'event_type_entity_id'),
        },
        validators: {
          inventory_items: [getInventoryItemVenuesRequireValidator()],
        },
      },
      formEventHandler,
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public onFormAction(action: IFormAction): void {}

  public getOnFormReady(
    formAddition: ITransferCreateFormAddition,
    options: ITransferCreateOptions,
  ): (form: FormGroup) => void {
    return (form: FormGroup): void => {
      this.form = form;
      const isFromSiteChangeable = options.isTransferIn;

      const itemsGridFilter$ =
        formAddition.itemsGridFilter$ ??
        observableOf<IItemsGridFilter<IQohInventoryItemVenue>>(null);

      const disableLoader = (): void => {
        formAddition.isLoadingItems$.next(false);
      };

      const inventoryGridAndItemsObs = observableCombineLatest([
        formAddition.itemsGrid$,
        form
          .get(isFromSiteChangeable ? 'site_id' : 'site_id_to')
          .valueChanges.pipe(rx.distinctUntilChanged()),
        formAddition.gridApi$,
      ]).pipe(
        rx.tap(() => {
          setFormFieldValue(
            form,
            TRANSFER_FORM_FIELD_PATHS.inventoryItemVenues,
            [],
            false,
          );
          formAddition.isLoadingItems$.next(true);
          formAddition.replenishButtonDisabled$.next(true);
          formAddition.clearQtyRequestBtnDisable$.next(true);
        }),
        rx.switchMap(([inventoryItemGrid, curSiteId, gridApi]) =>
          this.getInventoryItemVenuesObs([
            isFromSiteChangeable ? curSiteId : options.site_id,
            isFromSiteChangeable ? options.site_id : curSiteId,
          ]).pipe(
            rx.map(
              (items: IQohInventoryItemVenue[]): IQohInventoryItemVenue[] =>
                map(items, (item) => {
                  const baseMeasure = find(
                    get(item, 'origin_measures'),
                    'is_base',
                  );
                  const baseUnit = getBaseMeasure(
                    {
                      description: get(baseMeasure, 'description'),
                      factor_value: get(baseMeasure, 'factor_value'),
                    },
                    this.numberFormatService.getNumberFormatter(false),
                    this.i18nService,
                  );
                  set(item, 'item_base_unit', baseUnit);

                  return item;
                }),
            ),
            rx.map((totalItems) => ({
              totalItems,
              gridApi,
              grid: inventoryItemGrid,
            })),
          ),
        ),
      );
      this.scanningService.scanOptions = {
        isCategoryListView: false,
        focusKeyForListModal: 'initial_count',
        focusKeyForTreeModal: 'initial_count',
        isCompleted: false,
        focusOnNextRow: false,
      };
      const scanningSubscription =
        this.scanningService.returnScanningSubscription(
          this.scanningService.scanOptions,
        );

      this.subscriptions.push(
        observableCombineLatest([inventoryGridAndItemsObs, itemsGridFilter$])
          .pipe(
            rx.tap(([inventoryGridAndItems, gridFilter]) => {
              const { totalItems, grid, gridApi } = inventoryGridAndItems;

              this.listGridApi = gridApi;
              this.allInventoryItems = totalItems;
              let filterOutInventoryItems = totalItems;

              if (isFunction(gridFilter?.itemShowsCondition)) {
                filterOutInventoryItems = gridFilter.itemShowsCondition(
                  totalItems,
                  grid,
                );
              }

              // when is empty should send replenish disabled
              formAddition.replenishButtonDisabled$?.next(
                isEmpty(filterOutInventoryItems),
              );
              formAddition.clearQtyRequestBtnDisable$?.next(
                isEmpty(filterOutInventoryItems),
              );

              setFormFieldValue(
                form,
                TRANSFER_FORM_FIELD_PATHS.inventoryItemVenues,
                filterOutInventoryItems,
                false,
              );
            }),
          )
          .subscribe(disableLoader, disableLoader),
      );

      this.subscriptions.push(
        getFormControl(form, TRANSFER_FORM_FIELD_PATHS.inventoryItemVenues)
          .valueChanges.pipe(rx.distinctUntilChanged())
          .subscribe((items: IQohInventoryItemVenue[]) => {
            // clear inline labels
            this.allInventoryItems = map(this.allInventoryItems, (item) => {
              const newItem = find(items, {
                entity_id: item.entity_id,
              });

              return isNil(newItem) ? item : newItem;
            });

            formAddition.inlineLabel$.next([]);
          }),
      );
      this.subscriptions.push(scanningSubscription);
    };
  }

  public async handleFooterAction(
    confirmed: boolean,
    modalValue: ITransfer,
    form: FormGroup,
  ): Promise<boolean> {
    if (!confirmed) {
      if (form.dirty) {
        const leaveConfirmOptions: IModalDialogOptions = {
          title: MODAL_TEMPLATES.MODAL_TEMPLATE_CONFIRM.title,
          text: EDITOR_MESSAGES.customConfirmation({
            entityDescription: this.i18nService.instant(
              I18n.Forms.TransferForm.CreateSpecificTypeTransfer,
              {
                type: this.i18nService.instant(
                  I18n.Forms.TransferForm.DuringEvent,
                ),
              },
            ),
            i18nService: this.i18nService,
          }),
          buttons: [BTN_YES, BTN_CANCEL],
        };
        const { confirmed: leaveConfirmed }: IModalDialogResult =
          await this.uiActions.confirm(leaveConfirmOptions);

        return leaveConfirmed;
      }

      return true;
    }

    const userConfirmModalOptions: IModalDialogOptions = {
      title: MODAL_TEMPLATES.MODAL_TEMPLATE_CONFIRM.title,
      text: this.i18nService.translate(
        I18n.InfoMessages.AreYouSureYouWantToDo,
        {
          wantTo: this.i18nService.translate(
            I18n.Forms.TransferForm.submitAndComplete,
          ),
          target: this.i18nService.translate(
            I18n.Forms.TransferForm.ThisUpperCaseTransfer,
          ),
        },
      ),
      buttons: [BTN_YES, BTN_CANCEL],
    };

    const { confirmed: submitConfirmed } = await this.uiActions.promptFormModal(
      userConfirmModalOptions,
    );

    if (!submitConfirmed) {
      return false;
    }

    this.uiActions.enableLoader();

    const transferDoc = {
      ...pick(modalValue, [
        'event_id',
        'site_id',
        'site_id_to',
        'transfer_type',
        'virtual.transaction_date',
      ]),
      type: 'transfer',
      status: TRANSFER_STATUS_VALUE.Completed,
      curStatus: TRANSFER_STATUS_VALUE.Completed,
      transaction_id: generateObjectId(),
      inventory_items: chain(get(modalValue, 'inventory_items'))
        .map((item) => {
          const initialCount = parseFloat(
            `${get(item, 'initial_count', null)}`,
          );

          if (initialCount) {
            return {
              initial_count: initialCount,
              item_entity_id: item.entity_id,
              measure_entity_id: item.measure_entity_id,
              _id: get(item, '_id') || generateObjectId(),
            };
          }

          return null;
        })
        .compact()
        .value(),
    };

    try {
      await this.transferService
        .createEntity(transferDoc)
        .pipe(rx.first())
        .toPromise();

      this.uiActions.showToasterMessage({
        type: NotificationType.SUCCESS,
        title: TOASTER_SUCCESS_MESSAGE.title,
        body: I18n.Forms.TransferForm.TransferCompleted,
      });

      return true;
    } catch (error) {
      this.uiActions.showToasterMessage({
        type: NotificationType.ERROR,
        title: TOASTER_ERROR_MESSAGE.title,
        body: this.i18nService.instant(I18n.ErrorMessages.FailedToDo, {
          wantTo: this.i18nService.instant(
            I18n.Forms.TransferForm.createTransfer,
          ),
        }),
      });

      return false;
    } finally {
      this.uiActions.disableLoader();
    }
  }

  public getInlineChangeMapper(
    options: ITransferCreateOptions,
  ): (value: GridRowData, payload: XuxGridActionPayload) => GridRowData {
    return (value: GridRowData, payload: XuxGridActionPayload): GridRowData => {
      let newGridRowData: GridRowData;
      const eventTypeEntityId = getFormFieldValue(
        this.form,
        'virtual.event_type_entity_id',
      );

      switch (payload.field) {
        case TRANSFER_FORM_FIELD_PATHS.measureEntityId:
          const updatedRowData: GridRowData = handleTransferUnitChange(
            value,
            { ...options.event, event_type_entity_id: eventTypeEntityId },
            false,
          );

          newGridRowData = updatedRowData;
          break;
        case TRANSFER_FORM_FIELD_PATHS.initialCount:
          const {
            initial_count,
            factorValue,
          }: Pick<InitialFactorAndCountValue, 'initial_count' | 'factorValue'> =
            updateInitialFactorAndCountValue(value);

          newGridRowData = {
            ...value,
            initialFactorAndCountValue: merge(
              {},
              value.initialFactorAndCountValue,
              { initial_count, factorValue },
            ),
          };
          break;
        default:
          newGridRowData = value;
          break;
      }

      return newGridRowData;
    };
  }

  public getSiteDataSource(options: ITransferCreateOptions): DataSource {
    const dataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.SITE,
    );
    if (dataSource) {
      dataSource.service.quickFilterFields = ['name', 'store_number'];
    }
    dataSource.fetchList = this.getSiteDataSourceFetchList(
      dataSource,
      options,
    ).bind(this);

    return dataSource;
  }

  public getSiteDataSourceFetchList(
    dataSource: DataSource,
    options: ITransferCreateOptions,
  ): (query: IFilterQuery) => Promise<ISiteResponse> {
    return (query: IFilterQuery): Promise<ISiteResponse> => {
      const queryWithDefaults: IFilterQuery = merge(
        {
          filter: `is_active eq true`,
          select: [
            'name',
            'id',
            'type',
            'business_type',
            'store_number',
            'metadata',
          ],
          filter_by_assigned_sites: true,
          include_inactive: false,
        },
        query,
        {
          top: MAX_PAGE_SIZE,
        },
      );

      return observableCombineLatest([
        this.sitesService.fetchList(queryWithDefaults),
        this.dmDataService.getSitesPreferencesBySiteId(options.site_id),
      ])
        .pipe(
          rx.first(),
          rx.map(([sitesResponse, sitePreference]) => {
            if (!this.form) {
              return sitesResponse;
            }

            const event: IEvent = get(options, 'event');
            const eventSites: ISite[] = get(event, 'available_sites', []);
            let sites: ISite[] = sitesResponse.items as ISite[];

            sites = getEventRelatedAndWarehouseSites(sites, eventSites);

            sites = setPrimaryAndWarehouseTop(sites);

            // filter out current site
            sites = filter(sites, (site) => !isEqual(options.site_id, site.id));

            const siteWarehouseId = get(
              sitePreference,
              'preferences.back_office.warehouse.primary_warehouse',
            );

            const warehouseSite: ISite = isEmpty(siteWarehouseId)
              ? null
              : find(sites, {
                  id: siteWarehouseId,
                });

            if (isSiteTypePrimaryWarehouse(warehouseSite)) {
              setFormFieldValue(
                this.form,
                options.isTransferIn ? 'site_id' : 'site_id_to',
                get(warehouseSite, 'id'),
                false,
              );
            }
            sitesResponse.items = addStoreNumberForSites(sites);
            // we get all sites now form query(top: MAX_PAGE_SIZE), so we need sync total count with filtered items
            sitesResponse.total = size(sitesResponse.items);

            return sitesResponse;
          }),
        )
        .toPromise()
        .then(
          (result) => result,
          (error) => (dataSource.error.next(error), Promise.reject(error)),
        );
    };
  }

  public async onClickReplenish(
    replenishBtn: ToolbarButtonDefinition,
    options: ITransferCreateOptions,
    formAddition: ITransferCreateFormAddition,
  ) {
    const replenishConfig =
      REPLENISH_CONFIG_MAPPING[replenishBtn?.caption as string];

    const eventTypeEntityId = getFormFieldValue(
      this.form,
      'virtual.event_type_entity_id',
    );

    const selectedEvent = {
      ...options.event,
      event_type_entity_id: eventTypeEntityId,
    };

    const filteredItems: IQohInventoryItemVenue[] = map(
      getAllRowsAfterFilterAndSort(this.listGridApi),
      'data',
    );

    const items = getFormFieldValue(
      this.form,
      TRANSFER_FORM_FIELD_PATHS.inventoryItemVenues,
    );

    const haveSearch: boolean = this.listGridApi.isAnyFilterPresent(); // true have; false no
    const noFilter: boolean = !haveSearch && this.isShowAllItems;
    const filteredNone: boolean = haveSearch && isEmpty(filteredItems);
    const disabledReplenishOption: boolean = noFilter || filteredNone;

    await this.replenishWorkerBuilder
      .build({
        additionDataOpts: {
          preInventory: [
            () => ({
              event: selectedEvent,
            }),
          ],
        },
        on: {
          afterReplenish: (replenishRes) => {
            const { destinations, replenishParams } = replenishRes;
            const replenishItemsScope: REPLENISH_SCOPE_OPTIONS_VALUES = get(
              replenishParams,
              'replenishItemsScope',
              REPLENISH_SCOPE_OPTIONS_VALUES.ALL_ITEMS,
            );

            const replenishedDestination =
              destinations as IReplenishDestination;

            if (replenishedDestination.noAvailablePAR) {
              this.sendReplenishLabel(
                ReplenishType.Par,
                {
                  resultType: ReplenishResultType.Failed,
                },
                formAddition,
              );

              return;
            }

            const replenishedItems: IQohInventoryItemVenue[] =
              replenishedDestination.inventory_items;
            let updateToFormItems: IQohInventoryItemVenue[] = replenishedItems;
            const isSelectAll: boolean = isEqual(
              replenishItemsScope,
              REPLENISH_SCOPE_OPTIONS_VALUES.ALL_ITEMS,
            );

            if (isSelectAll && !this.isShowAllItems) {
              formAddition.showItemsCaptionChange$.next(true);
            }

            if (haveSearch && !isSelectAll) {
              updateToFormItems = map(items, (item) => {
                const replenishItem = find(replenishedItems, {
                  entity_id: item.entity_id,
                });

                return isNil(replenishItem) ? item : replenishItem;
              });
            }

            setFormFieldValue(
              this.form,
              TRANSFER_FORM_FIELD_PATHS.inventoryItemVenues,
              updateToFormItems,
              !isEqual(items, updateToFormItems),
            );

            this.sendReplenishLabel(
              ReplenishType.Par,
              {
                resultType: ReplenishResultType.Success,
              },
              formAddition,
            );
          },
        },
      })
      .replenish({
        destination: {
          disabledReplenishOption,
          fromSite: getFormFieldValue(this.form, 'site_id'),
          toSite: getFormFieldValue(this.form, 'site_id_to'),
          all_inventory_items: this.allInventoryItems,
          filtered_inventory_items: filteredItems,
        },
        strategy: replenishConfig.strategy,
        confirmModalOptions: {
          modalTitle: replenishConfig.modalTitle,
        },
      });
  }

  public sendReplenishLabel(
    replenishType: ReplenishType,
    replenishResult: IReplenishResult,
    formAddition: ITransferCreateFormAddition,
  ): void {
    if (isEqual(ReplenishResultType.Cancel, replenishResult.resultType)) {
      return;
    }

    const replenishLabelMapping: Partial<
      Record<ReplenishResultType, IInlineLabelOptions[]>
    > = {
      [ReplenishResultType.Success]: [
        {
          widgetData: {
            title: this.i18nService.translate(
              I18n.InfoMessages.Didsuccessfully,
              {
                did: this.i18nService.translate(I18n.Status.Replenished),
              },
            ),
            itemClass: 'full-width info',
          },
        },
      ],
      [ReplenishResultType.Failed]: [
        {
          widgetData: {
            title: isEqual(ReplenishType.Par, replenishType)
              ? this.i18nService.instant(
                  I18n.ErrorMessages.ActionCannotBeDoneMessage,
                  {
                    reason: this.i18nService.instant(
                      I18n.ErrorMessages.NoAvailablePAR,
                    ),
                  },
                )
              : this.i18nService.instant(
                  I18n.ErrorMessages.ActionCannotBeDoneMessage,
                  {
                    reason: this.i18nService.instant(
                      I18n.ErrorMessages.NoAvailableMin,
                    ),
                  },
                ),
            itemClass: 'full-width hc-warning',
          },
        },
      ],
    };

    formAddition.inlineLabel$.next(
      replenishLabelMapping[replenishResult.resultType],
    );
  }

  public getMeasureEntityIdCellRendererParams(
    params: ICellRendererParams,
    visibleRowCount: number = VISIBLE_TRANSFER_GRID_ROW_COUNT,
  ): ListColumn['cellRendererParams'] {
    const measures = params.data.measures;
    if (isEmpty(measures)) {
      return null;
    }

    const rowCount = params.api.getDisplayedRowCount();
    const isLastRow = params.rowIndex === rowCount - 1;
    const isSecondToLastRow =
      params.rowIndex === rowCount - 2 && measures.length >= 2;
    const isThirdToLastRow =
      params.rowIndex === rowCount - 3 && measures.length >= 3;
    // We take into account the last 3 rows, since the max dropdown height is 3 items.
    // (visibleRowCount - 1) - to cover the case when 4 out of 5 rows are displayed on the grid and
    // the last item in the dropdown does not fit in the grid.
    const isPositionTop =
      (isLastRow && rowCount >= visibleRowCount - 1 && measures.length >= 2) ||
      ((isSecondToLastRow || isThirdToLastRow || isLastRow) &&
        rowCount >= visibleRowCount);

    return {
      isPositionTop,
      options: measures,
      disabled: false,
      value: params.data.measure_entity_id,
    };
  }

  public processInventoryItems(
    inventoryRes: IBaseEntityResponse,
  ): IBaseEntityResponse {
    forEach(inventoryRes.items, (inventoryItem: IQohInventoryItemVenue) => {
      const measures = inventoryItem.measures || [];
      const transferMeasures = filter(
        measures,
        (item) => item.transactions.transfer,
      );
      // sort the transfer Unit by description
      const sortMeasures = sortBy(
        map(transferMeasures, (filterItem) => {
          return {
            ...filterItem,
            text: generateUnitTextV2(filterItem, {
              numberFormatService: this.numberFormatService,
              measureTransactionType: MEASURE_TRANSACTION.Transfer,
            }),
            value: filterItem.entity_id,
            description: filterItem.description,
          };
        }),
        (item) => toLower(item.description),
      );

      set(inventoryItem, 'origin_measures', measures);
      set(inventoryItem, 'measures', sortMeasures);

      inventoryItem.measure_entity_id = get(
        getDefaultMeasure(sortMeasures, MEASURE_TRANSACTION.Transfer),
        ENTITY_ID_PATH,
      );
      inventoryItem.initial_count = undefined;
    });

    return inventoryRes;
  }

  /**
   * @deprecated should use takeIntersectionAndProcessThreshold in the replenish utils instead
   */
  public takeIntersectionAndProcessThreshold(
    res: IBaseEntityResponse,
    siteIdTo: string,
  ): IQohInventoryItemVenue[] {
    const itemCountDictionary = countBy(res.items, 'entity_id');

    return filter(
      res.items,
      (item: IQohInventoryItemVenue) =>
        itemCountDictionary[item.entity_id] > 1 &&
        isEqual(item.site_id, siteIdTo),
    ) as IQohInventoryItemVenue[];
  }

  public getQohFromVisible(formSiteId: EntityId): Promise<boolean> {
    return this.sitesService
      .fetchList({
        filter: `_id eq objectid(${formSiteId})`,
        select: ['type', 'business_type'],
      })
      .pipe(
        rx.map((response) => {
          const isWarehouseSite = isSiteTypeWarehouse(
            first(response.items as ISite[]),
          );

          return isWarehouseSite
            ? this.stateService.getTransferDisplayQohFrom()
            : true;
        }),
        rx.first(),
      )
      .toPromise();
  }

  public getQuantityOnHandDataObs(
    inventoryItemVenues: IQohInventoryItemVenue[],
    siteId: string,
    siteIdTo: string,
  ): Observable<
    | IBaseEntityType
    | IQuantityOnHandResponse
    | IBaseEntityResponse<IBaseEntityType>
  > {
    if (isEmpty(inventoryItemVenues)) {
      return observableOf(null);
    }

    return fromPromise(this.getQohFromVisible(siteId)).pipe(
      rx.switchMap((isQohFromVisible) =>
        this.transferService.getQuantityOnHandData(
          inventoryItemVenues,
          isQohFromVisible ? siteId : '',
          siteIdTo,
          ENTITY_ID_PATH,
        ),
      ),
      rx.catchError(this.catchHttpError),
    );
  }

  public getPendingQuantity(
    siteId: string,
    siteIdTo: string,
  ): Observable<IBaseEntityType> {
    return this.transferService
      .fetchList(
        {
          filter: `status eq 'pending' and ((site_id eq in(${siteId},${siteIdTo})) or (site_id_to eq in(${siteId},${siteIdTo})))`,
          select: [
            'site_id',
            'site_id_to',
            'inventory_items.item_entity_id',
            'inventory_items.initial_count',
            'inventory_items.measure_factor_value',
          ],
        },
        {
          skipMappers: true,
        },
        true,
      )
      .pipe(
        rx.map((res) => {
          return createPendingQuantityMap(
            {
              formFieldPath: {
                siteId: 'site_id',
                siteIdTo: 'site_id_to',
                initialCount: 'initial_count',
                measureFactorValue: 'measure_factor_value',
              },
            } as any,
            res,
            siteId,
            siteIdTo,
          );
        }),
        rx.catchError(this.catchHttpError),
      );
  }

  public getInventoryItemVenuesObs([
    siteId,
    siteIdTo,
  ]: FromAndToSiteId): Observable<IQohInventoryItemVenue[]> {
    return observableCombineLatest([
      this.fetchInventoryItemVenues([siteId, siteIdTo]).pipe(
        rx.map((res: IBaseEntityResponse) => this.processInventoryItems(res)),
        rx.map((res: IBaseEntityResponse<InventoryItemVenue>) => {
          const inventoryItemVenues = this.takeIntersectionAndProcessThreshold(
            res,
            siteIdTo,
          );

          return <IBaseEntityResponse<InventoryItemVenue>>{
            ...res,
            items: inventoryItemVenues,
            total: inventoryItemVenues.length,
          };
        }),
        rx.map((res: IBaseEntityResponse) =>
          map(
            res.items,
            (item) =>
              <IQohInventoryItemVenue>{
                ...item,
                initial_count: decimalNumber(0),
                item_cost: calculateUnitCost({ data: { ...item } }),
                total_cost: decimalNumber(0),
                qoh_from: decimalNumber(0),
                qoh_to: decimalNumber(0),
              },
          ),
        ),
        rx.mergeMap((processedItems: IQohInventoryItemVenue[]) =>
          this.getQuantityOnHandDataObs(processedItems, siteId, siteIdTo).pipe(
            rx.map((qohDataRes) => [processedItems, qohDataRes?.items]),
          ),
        ),
      ),
      this.getPendingQuantity(siteId, siteIdTo),
    ]).pipe(
      rx.map(([itemsAndQoh, pendingQtyMap]) => {
        const [processedItems, qohList] = itemsAndQoh;

        return this.mapToCreateItems(processedItems, qohList, pendingQtyMap);
      }),
      // apply chargeable and item name sorting
      rx.map((inventoryItems) =>
        partitionAndSortItemsByChargeable(inventoryItems, 'is_chargeable'),
      ),
      rx.map((res: IQohInventoryItemVenue[]) =>
        appendInitialFactorAndCountValue(res),
      ),
    );
  }

  public fetchInventoryItemVenues([
    siteId,
    siteIdTo,
  ]: FromAndToSiteId): Observable<IBaseEntityResponse> {
    const query = {
      filter: 'measures/transactions/transfer eq in(true)',
      select: [
        'is_chargeable',
        'code',
        'reporting_category_major',
        'reporting_category_minor',
        'name',
        'gl_account',
        'measures',
        'vendors',
        'preferred_vendor',
        'thresholds',
        'costs',
        'upc_codes',
      ],
    };

    return this.inventoryItemVenueService.fetchCurrentList(query, [
      siteId,
      siteIdTo,
    ]);
  }

  public mapToCreateItems(
    inventoryItems: IQohInventoryItemVenue[],
    qohList: IQohInventoryItemVenue[],
    pendingQtyMap: Record<string, {}>,
  ): IQohInventoryItemVenue[] {
    const qohAndPendingQty = {
      qohList,
      pendingQtyMap,
    };

    return map(inventoryItems, (item) => {
      const qohTo = this.getQohAndPendingQuantity(
        item,
        'site_id_to',
        qohAndPendingQty,
      );
      const qohFrom = this.getQohAndPendingQuantity(
        item,
        'site_id',
        qohAndPendingQty,
      );

      return { ...item, qohTo, qohFrom, initial_count: null };
    });
  }

  public async openClearQtyModal(
    text: string,
    fieldPath: string[],
  ): Promise<void> {
    const modalOptions = {
      text: this.i18nService.instant(
        I18n.Forms.TransferForm.AreYouSureYouWantToClearAllType,
        {
          type: this.i18nService.translate(text),
        },
      ),
    };
    const modalResult = await this.uiActions.confirm(modalOptions);
    if (!modalResult.confirmed) return;
    const result = this.transferQtyService.clearField(
      fieldPath,
      getFormFieldValue(
        this.form,
        TRANSFER_FORM_FIELD_PATHS.inventoryItemVenues,
      ),
    );
    setFormFieldValue(
      this.form,
      TRANSFER_FORM_FIELD_PATHS.inventoryItemVenues,
      result,
      true,
    );
  }

  /**
   * @deprecated should use getQohAndPendingQuantity in replenish utils instead
   */
  public getQohAndPendingQuantity(
    item: IQohInventoryItemVenue,
    path: string,
    {
      qohList,
      pendingQtyMap,
    }: { qohList: IQohInventoryItemVenue[]; pendingQtyMap: Record<string, {}> },
    siteId = getFormFieldValue(this.form, path),
  ): string[] {
    const qohDataForCurrentRow = getCurrentItemQoh(qohList, siteId, item);
    const pendingQtyForCurrentRow = get(pendingQtyMap, item.entity_id);

    return [
      get(qohDataForCurrentRow, 'count', 0)?.toString(),
      get(pendingQtyForCurrentRow, path, 0)?.toString(),
    ];
  }

  public catchHttpError = (
    error: IAPIErrorType,
  ): ObservableInput<IBaseEntityType> => {
    const toast = {
      errors: {
        title: error.title,
        message: error?.meta?.message || '',
      },
    };
    this.uiActions.showErrorToasterMessage(toast);

    return observableOf({ items: [] });
  };

  public getTransferGridReadyHandler(action: XuxGridAction): void {
    this.scanningService.gridApi = action.payload?.gridApi;
    this.scanningService.columnApi =
      action?.payload?.gridApi?.['columnController']?.columnApi;
  }
}
